import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { Box, Button, Grid } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";

import CustomDataGrid from "./components/CustomDataGrid";
import useTenant from "../../../hooks/useTenant";
import { useDialog } from "../../../contexts/DialogContext";
import { getTimeStamp } from "../../../utils/common";
import Upload from "./components/Upload";
import useSetting from "../../../hooks/useSetting";
import useAuth from "../../../hooks/useAuth";

function DataGridPage() {
  const { getAllTenant, getTenant, tenants, paging, isLoading, downloadFile } = useTenant();

  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;
  const { getTenantSetting } = useSetting();
  const navigate = useNavigate();
  const { user } = useAuth();
  const canDelete =
    user && ["SUPER_ADMIN", "ADMIN", "MULTI_SERVICE_OPERATOR"].includes(user.businessType);
  const { t } = useTranslation();
  const { openDialog } = useDialog();
  let [searchParams, setSearchParams] = useSearchParams();
  const { state } = useLocation();

  const currentPayload = {
    filters: [],
    pagination: {
      pageNumber: 1,
      pageSize: 100,
    },
    sortFields: [
      {
        field: "tenantName",
        sort: "ASC",
      },
    ],
  };

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);

  const [payload, setPayload] = useState(currentPayload);

  const fetchData = async () => {
    await getAllTenant(payload);
  };

  const refreshPage = () => {
    setPayload(currentPayload);
    filterResetFunc();
    setPaginationModel({
      page: 0,
      pageSize: 100,
    });
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");

    const pagination = {
      pageNumber: newPaginationModel.page === 0 ? 1 : newPaginationModel.page + 1,
      pageSize: newPaginationModel.pageSize, // Update pageSize dynamically
    };

    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 100,
  });

  const paginationMetaRef = useRef();

  // Memoize to avoid flickering when the `hasNextPage` is `undefined` during refetch
  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  const isInitialMount = useRef(true);
  useEffect(() => {
    fetchData();
  }, [payload]);

  const addTenant = () => {
    navigate("/cpanel/tenant/add");
  };
  const editTenant = (id, actionType) => {
    switch (actionType) {
      case "edit":
        navigate("/cpanel/tenant/edit", { state: { id: id } });
        break;
      case "general_settings":
        navigate("/cpanel/tenant/general-setting", { state: { id: id } });
        break;
      case "email_settings":
        navigate("/cpanel/tenant/email-setting", { state: { id: id } });
        break;
      case "sms_settings":
        navigate("/cpanel/tenant/sms-setting", { state: { id: id } });
        break;
    }
    console.log(id, "actionType", actionType);
  };

  const exportFile = async (fileType) => {
    await downloadFile(fileType, payload);
  };
  const openDownloadDialog = () => {
    return openDialog("upload_tenant", "Upload Tenant", <Upload key={getTimeStamp()} />);
  };

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const [filterResetFunc, setFilterResetFunc] = useState(
    () => () => console.log("Refresh initialized"),
  );

  return (
    <React.Fragment>
      <Grid container spacing={6}>
        {canDelete && (
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end">
              <Button
                variant="outlined"
                startIcon={<HistoryIcon />}
                onClick={() => navigate("/cpanel/tenant/delete-history")}
              >
                Delete History
              </Button>
            </Box>
          </Grid>
        )}
        <Grid item xs={12}>
          <CustomDataGrid
            trans={t}
            addPage={addTenant}
            editPage={editTenant}
            refreshPage={refreshPage}
            downloadFile={exportFile}
            setFilterResetFunc={setFilterResetFunc}
            openDownloadDialog={openDownloadDialog}
            id="tenantId"
            isLoading={isLoading}
            sortModel={sortModel}
            handleSortModelChange={handleSortModelChange}
            setPayload={setPayload}
            payload={payload}
            rows={tenants}
            rowCount={totalNumberOfRecords}
            onPaginationModelChange={onPageChange}
            paginationMeta={paginationMeta}
            paging={{
              pageNumber: pageNumber,
              pageSize: pageSize,
            }}
            paginationModel={paginationModel}
          />
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default DataGridPage;
